import React, { useEffect, useState } from 'react';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import { canManageCrmSalaries } from '../../../utils/crmPermissions';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { useToast } from '../../../hooks/use-toast';
import { downloadBlob } from '../../../utils/blobDownload';
import { formatDate } from '../../../utils/appDateTime';

export default function CrmPayslips() {
  const { user } = useAuth();
  const isAdmin = canManageCrmSalaries(user?.role_slug);
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/crm/salaries');
      setItems(res.data?.data?.items || []);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (id) => {
    try {
      const res = await api.get(`/crm/payroll/payslips/${id}/download`, { responseType: 'blob' });
      await downloadBlob(res.data, `payslip-${id}.pdf`);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Download failed', variant: 'destructive' });
    }
  };

  const printSlip = async (id) => {
    try {
      const res = await api.get(`/crm/payroll/payslips/${id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const w = window.open(url);
      if (w) {
        w.onload = () => w.print();
      }
    } catch (e) {
      toast({ title: 'Error', description: 'Print failed', variant: 'destructive' });
    }
  };

  const emailSlip = async (id) => {
    try {
      await api.post(`/crm/payroll/payslips/${id}/email`);
      toast({ title: 'Emailed', description: 'Payslip email sent' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  if (loading) return <div className="text-muted-foreground">Loading payslips…</div>;

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Payslips</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="py-2">Employee</th>
              <th>Period</th>
              <th>Net</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-b">
                <td className="py-2">{row.first_name} {row.last_name}</td>
                <td>{formatDate(row.period_start)} – {formatDate(row.period_end)}</td>
                <td>₹{Number(row.net_salary || row.final_salary || 0).toLocaleString('en-IN')}</td>
                <td>{row.workflow_status || row.payment_status}</td>
                <td className="space-x-1 whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => download(row.id)}>PDF</Button>
                  <Button size="sm" variant="outline" onClick={() => printSlip(row.id)}>Print</Button>
                  {isAdmin && (
                    <Button size="sm" variant="outline" onClick={() => emailSlip(row.id)}>Email</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="text-muted-foreground text-sm py-4">No payslips yet</p>}
      </CardContent>
    </Card>
  );
}
